import React from 'react';
import { Alarm, AlarmSeverity, AlarmStatus, AlarmType } from '@/api/alarms/types';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, AlertCircle, Info, CheckCircle, Clock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AlarmListProps {
  alarms: Alarm[];
  selectedAlarmId: string | null;
  onSelectAlarm: (alarm: Alarm) => void;
  isLoading: boolean;
  onRefresh: () => void;
}

export function AlarmList({ alarms, selectedAlarmId, onSelectAlarm, isLoading, onRefresh }: AlarmListProps) {
  if (isLoading) {
    return (
      <div className="h-full">
        <div className="divide-y divide-light-grey">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="flex items-start p-3" style={{ animationDelay: `${index * 100}ms` }}>
              <div className="flex-shrink-0 mr-3 mt-0.5">
                <div className="w-4 h-4 bg-gray-200 rounded-full animate-skeleton"></div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start mb-2">
                  <div className="animate-skeleton h-3 bg-gray-200 rounded w-24"></div>
                  <div className="animate-skeleton h-2 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="animate-skeleton h-3 bg-gray-200 rounded w-full mb-2"></div>
                <div className="flex items-center space-x-2">
                  <div className="animate-skeleton h-4 bg-gray-200 rounded w-12"></div>
                  <div className="animate-skeleton h-4 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex justify-center items-center mt-6 gap-3">
          <div className="flex gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 bg-primary rounded-full breathe-dots"
              />
            ))}
          </div>
          <p className="text-xs text-gray-600 font-medium">Loading alarms...</p>
        </div>
      </div>
    );
  }

  if (alarms.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="text-gray-400 mb-2">
          <AlertCircle className="h-12 w-12 mx-auto stroke-[1.5px]" />
        </div>
        <h3 className="text-lg font-medium text-gray-700 mb-1">No alarms found</h3>
        <p className="text-sm text-gray-500">
          There are no alarms matching your current filters.
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          className="mt-4 text-primary hover:bg-blue-50"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" />
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className="divide-y divide-light-grey">
        {alarms.map((alarm) => (
          <AlarmListItem
            key={alarm.id}
            alarm={alarm}
            isSelected={alarm.id === selectedAlarmId}
            onClick={() => onSelectAlarm(alarm)}
          />
        ))}
      </div>
    </div>
  );
}

interface AlarmListItemProps {
  alarm: Alarm;
  isSelected: boolean;
  onClick: () => void;
}

function AlarmListItem({ alarm, isSelected, onClick }: AlarmListItemProps) {
  return (
    <div
      className={cn(
        "flex items-start p-3 cursor-pointer transition-colors",
        isSelected ? "bg-white-blue" : "hover:bg-bg"
      )}
      onClick={onClick}
    >
      <div className="flex-shrink-0 mr-3 mt-0.5">
        <AlarmIcon severity={alarm.severity} status={alarm.status} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start">
          <h4 className="text-xs font-medium text-black truncate">
            {alarm.deviceName}
          </h4>
          <span className="text-[10px] text-dark-grey">
            {formatDistanceToNow(new Date(alarm.timestamp), { addSuffix: true })}
          </span>
        </div>
        <p className="text-xs text-black truncate mt-0.5">
          {alarm.message}
        </p>
        <div className="flex items-center mt-1 space-x-2">
          <AlarmTypeBadge type={alarm.type} />
          <AlarmStatusBadge status={alarm.status} />
          <span className="text-xs text-dark-grey">{alarm.code}</span>
        </div>
      </div>
    </div>
  );
}

function AlarmIcon({ severity, status }: { severity: AlarmSeverity; status: AlarmStatus }) {
  if (status === 'resolved') {
    return <CheckCircle className="h-5 w-5 text-success" />;
  }

  if (status === 'acknowledged') {
    return <Clock className="h-5 w-5 text-primary" />;
  }

  switch (severity) {
    case 'critical':
      return <AlertCircle className="h-5 w-5 text-danger" />;
    case 'warning':
      return <AlertTriangle className="h-5 w-5 text-light-orange" />;
    case 'info':
      return <Info className="h-5 w-5 text-primary" />;
    default:
      return null;
  }
}

function AlarmTypeBadge({ type }: { type: AlarmType }) {
  // Use a more subtle styling for all types
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-[5px] text-xs font-medium border border-light-grey bg-white text-black">
      {type}
    </span>
  );
}

function AlarmStatusBadge({ status }: { status: AlarmStatus }) {
  // Use a more subtle styling with just a thin colored border to indicate status
  const statusConfig = {
    active: { border: 'border-danger', dot: 'bg-danger' },
    acknowledged: { border: 'border-primary', dot: 'bg-primary' },
    resolved: { border: 'border-success', dot: 'bg-success' },
  };

  const config = statusConfig[status];

  return (
    <span className={cn(
      "inline-flex items-center px-2 py-0.5 rounded-[5px] text-xs font-medium border bg-white text-black",
      config.border
    )}>
      <span className={cn("w-1.5 h-1.5 rounded-full mr-1", config.dot)}></span>
      {status}
    </span>
  );
}
